import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@cloudscape-design/components/button';
import DatePicker from '@cloudscape-design/components/date-picker';
import FormField from '@cloudscape-design/components/form-field';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { Period, PeriodPreset } from '@etiquetachef/types';
import { isValidRange, presetToRange } from '@/lib/period';

// O SegmentedControl do Cloudscape só renderiza 2–6 segmentos numa linha
// (o grid interno não tem coluna p/ >6). Mantemos 6 presets; `M12`/`MTD`
// continuam válidos no tipo `PeriodPreset` e via URL, só saíram dos botões.
const PRESETS: { id: PeriodPreset; labelKey: string }[] = [
  { id: 'TODAY', labelKey: 'bi.period.presetToday' },
  { id: 'D7', labelKey: 'bi.period.preset7d' },
  { id: 'D30', labelKey: 'bi.period.preset30d' },
  { id: 'D90', labelKey: 'bi.period.preset90d' },
  { id: 'YTD', labelKey: 'bi.period.presetYtd' },
  { id: 'CUSTOM', labelKey: 'bi.period.presetCustom' },
];

export type PeriodControlProps = {
  value: Period;
  onChange: (period: Period) => void;
};

/**
 * Seletor de período do BI super-admin, 100% Cloudscape (substitui o
 * `PeriodPicker` do `ui-shared`, que era shadcn/Tailwind). Presets em
 * `SegmentedControl` (colapsa para `Select` em telas estreitas) +
 * range custom com dois `DatePicker` ISO e botão Aplicar. A lógica
 * (presets, validação de range) vem de `@/lib/period` (portado do ui-shared).
 * Datas em BRT (`YYYY-MM-DD`),
 * sincronizadas com a URL pelo `usePeriod` do chamador.
 */
export function PeriodControl({ value, onChange }: PeriodControlProps): React.JSX.Element {
  const { t } = useTranslation();
  const [customFrom, setCustomFrom] = useState(value.from);
  const [customTo, setCustomTo] = useState(value.to);

  const rangeValid = isValidRange(customFrom, customTo);
  const customError =
    value.preset === 'CUSTOM' && !rangeValid ? t('bi.period.invalidRange') : null;

  /** Aplica um preset; `CUSTOM` reaproveita o range digitado. */
  function selectPreset(preset: PeriodPreset): void {
    if (preset === 'CUSTOM') {
      onChange({ preset, from: customFrom, to: customTo });
      return;
    }
    const range = presetToRange(preset);
    setCustomFrom(range.from);
    setCustomTo(range.to);
    onChange({ preset, ...range });
  }

  /** Aplica o range custom, só se válido. */
  function applyCustom(): void {
    if (!isValidRange(customFrom, customTo)) return;
    onChange({ preset: 'CUSTOM', from: customFrom, to: customTo });
  }

  const openCalendarAriaLabel = (selected: string | null): string =>
    selected ? `${t('bi.period.openCalendar')}, ${selected}` : t('bi.period.openCalendar');

  return (
    <SpaceBetween size="s">
      <SegmentedControl
        selectedId={value.preset}
        label={t('bi.period.label')}
        options={PRESETS.map((preset) => ({ id: preset.id, text: t(preset.labelKey) }))}
        onChange={({ detail }) => {
          selectPreset(detail.selectedId as PeriodPreset);
        }}
      />

      {value.preset === 'CUSTOM' && (
        <SpaceBetween size="s" direction="horizontal" alignItems="end">
          <FormField label={t('bi.period.from')}>
            <DatePicker
              value={customFrom}
              placeholder="YYYY/MM/DD"
              i18nStrings={{ openCalendarAriaLabel }}
              onChange={({ detail }) => {
                setCustomFrom(detail.value);
              }}
            />
          </FormField>
          <FormField label={t('bi.period.to')} errorText={customError ?? undefined}>
            <DatePicker
              value={customTo}
              placeholder="YYYY/MM/DD"
              i18nStrings={{ openCalendarAriaLabel }}
              onChange={({ detail }) => {
                setCustomTo(detail.value);
              }}
            />
          </FormField>
          <Button onClick={applyCustom} disabled={!rangeValid}>
            {t('bi.period.apply')}
          </Button>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}
